import { useMutation } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import { ExecutionResult } from 'graphql';
import { MutationResult } from '@apollo/react-common';
import {
  CreateTimelineEntryMutation,
  CreateTimelineEntryMutationVariables,
  ListTimelineEntriesQuery,
  CreateTimelineEntryInput,
  ImageMediaInput,
  VideoMediaInput,
  MeasurementSystem,
} from '../../../types/__generated__';
import { useNotNullableContext } from '../useNotNullableContext';
import { PageMetaContext } from '../../context/PageMetaContext';
import {
  readTimelineEntries,
  writeTimelineEntries,
} from './useListTimelineEntries';
import { TimelineImage } from '../../components/Timeline/timelineTypes';
import { buildQuery } from '../usePageQuery';
import { mediaCollectionFragment } from '../../fragments/mediaCollection';

export const CREATE_ENTRY = gql`
  mutation CreateTimelineEntry($input: CreateTimelineEntryInput!) {
    createTimelineEntry(input: $input) {
      id
      legacyId
      message
      media {
        ... on ImageMedia {
          id
          url
          legacyId
        }
      }
      type
      createdAt
    }
  }
`;

type CreateTimelineEntry = (
  entry: Pick<
    CreateTimelineEntryInput,
    'type' | 'pageId' | 'pageSlug' | 'message'
  > & {
    media: TimelineImage[];
  },
) => Promise<ExecutionResult<CreateTimelineEntryMutation>>;

export default function useCreateTimelineEntry(
  measurementSystem?: MeasurementSystem,
): [CreateTimelineEntry, MutationResult<CreateTimelineEntryMutation>] {
  const { pageType, pageShortName, isPreview } = useNotNullableContext(
    PageMetaContext,
  );

  const [runMutation, mutationResult] = useMutation<
    CreateTimelineEntryMutation,
    CreateTimelineEntryMutationVariables
  >(CREATE_ENTRY, {
    update(cache, { data }) {
      if (!data || !data.createTimelineEntry) {
        return;
      }

      const newTimelineEntry = {
        ...data.createTimelineEntry,
        fitnessActivity: null,
        refreshRequestedTime: null,
      };

      const result = readTimelineEntries(
        pageType,
        pageShortName,
        cache,
        measurementSystem,
      );

      const {
        previousPage,
        previousOwner,
        previousTimeline,
        previousPageInfo,
        previousTimelineNodes,
      } = result;

      const newTimelineData: ListTimelineEntriesQuery = {
        page: {
          ...previousPage,
          owner: previousOwner,
          timeline: {
            ...previousTimeline,
            pageInfo: previousPageInfo,
            nodes: [newTimelineEntry, ...previousTimelineNodes],
            totalCount: previousTimeline.totalCount + 1,
          },
        },
      };

      writeTimelineEntries(pageType, pageShortName, newTimelineData, cache);
    },
    context: {
      credentials: 'include',
    },
  });

  const createTimelineEntry: CreateTimelineEntry = entry => {
    return runMutation({
      variables: {
        input: {
          ...entry,
          media: entry?.media?.reduce<{
            imageMedia: ImageMediaInput[];
            videoMedia: VideoMediaInput[];
          }>(
            (acc, media) => {
              if (media.__typename === 'ImageMedia') {
                acc.imageMedia = [{ url: media.url }, ...acc.imageMedia];
              }
              return acc;
            },
            { imageMedia: [], videoMedia: [] },
          ),
        },
      },
      refetchQueries: [
        {
          query: buildQuery(mediaCollectionFragment),
          variables: {
            type: pageType.toUpperCase(),
            slug: pageShortName,
            preview: isPreview,
          },
        },
      ],
    });
  };

  return [createTimelineEntry, mutationResult];
}
